import { useCallback, useEffect, useState } from "react";
import {
  Badge, Button, Checkbox, Container, Group, Menu, Modal, NumberInput, Select, Stack, Tabs, Text, Textarea, Title,
} from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { apiErrorMessage } from "../helpers/apiError";
import HttpClient from "../helpers/HttpClient";
import {
  RENTAL_STATUS_COLORS, RENTAL_STATUS_LABELS, RentalRow, renterLabel,
} from "../types/inventory.type";
import { ArticleCategory } from "../types/article-category.type";
import { RentalDialog } from "../components/inventory/RentalDialog";

type Filter = "out" | "reserved" | "overdue" | "history";

const fmt = (d?: string | null) => (d ? dayjs(d).format("DD.MM.YY HH:mm") : "–");
const euro = (v: number) => `${v.toFixed(2).replace(".", ",")} €`;

// "Currently out" (incl. keys: filter by category "Schlüssel"), reservations,
// overdue and history — with the actions for each status.
function RentalList() {
  const [filter, setFilter] = useState<Filter>("out");
  const [rows, setRows] = useState<RentalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [problem, setProblem] = useState<{ rental: RentalRow; kind: "lost" | "damaged" } | null>(null);
  // ?category=Schlüssel preselects a category by name (navigation shortcut
  // "Schlüssel": the "Ausgegeben" tab then lists who holds which key).
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, string> = { filter };
    if (categoryId) params.categoryId = categoryId;
    new HttpClient<RentalRow>("/rental").getAll(params)
      .then(setRows)
      .catch(() => notifications.show({ color: "red", message: "Ausleihen konnten nicht geladen werden." }))
      .finally(() => setLoading(false));
  }, [filter, categoryId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    new HttpClient<ArticleCategory>("/article-category").getAll().then(setCategories).catch(() => {});
  }, []);

  // Follows the URL: switching between "Schlüssel" and "Verleih" in the
  // navigation keeps this page mounted, so the filter must be reset too.
  useEffect(() => {
    if (!categoryParam) {
      setCategoryId(null);
      return;
    }
    if (categories.length === 0) return;
    const match = categories.find((c) => c.name.toLowerCase() === categoryParam.toLowerCase());
    setCategoryId(match ? String(match.id) : null);
  }, [categoryParam, categories]);

  const selectedCategory = categories.find((c) => String(c.id) === categoryId);
  const isKeys = selectedCategory?.name.toLowerCase() === "schlüssel";

  const action = async (rental: RentalRow, name: "check-out" | "return" | "cancel", confirmText?: string) => {
    if (confirmText && !confirm(confirmText)) return;
    try {
      await new HttpClient(`/rental/${rental.id}`).postAction(name);
      notifications.show({
        color: "green",
        message: { "check-out": "Ausgegeben.", return: "Zurückgenommen.", cancel: "Reservierung storniert." }[name],
      });
      load();
    } catch (e) {
      notifications.show({ color: "red", message: apiErrorMessage(e, "Aktion fehlgeschlagen.") });
    }
  };

  return (
    <Container fluid mt="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>{isKeys ? "Schlüssel" : "Verleih & Reservierungen"}</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setDialogOpen(true)}>Reservieren / Ausgeben</Button>
      </Group>

      <Group justify="space-between" mb="sm" wrap="wrap">
        <Tabs value={filter} onChange={(v) => v && setFilter(v as Filter)}>
          <Tabs.List>
            <Tabs.Tab value="out">Ausgegeben</Tabs.Tab>
            <Tabs.Tab value="reserved">Reserviert</Tabs.Tab>
            <Tabs.Tab value="overdue" color="red">Überfällig</Tabs.Tab>
            <Tabs.Tab value="history">Verlauf</Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <Select
          placeholder="Alle Kategorien (z.B. Schlüssel)"
          clearable
          data={categories.map((c) => ({ value: String(c.id), label: c.name }))}
          value={categoryId}
          onChange={setCategoryId}
          w={260}
        />
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        fetching={loading}
        records={rows}
        noRecordsText="Keine Einträge"
        columns={[
          {
            accessor: "article",
            title: "Artikel",
            render: (r) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {r.quantity > 1 ? `${r.quantity}x ` : ""}{r.article.name}
                  {r.variant.label !== "Standard" ? ` (${r.variant.label})` : ""}
                </Text>
                {r.article.category && <Text size="xs" c="dimmed">{r.article.category.name}</Text>}
              </Stack>
            ),
          },
          { accessor: "renter", title: isKeys ? "Schlüsselinhaber" : "Bei", render: (r) => renterLabel(r) },
          {
            accessor: "plannedFrom",
            title: "Zeitraum",
            render: (r) => (
              <Text size="sm" c={r.isOverdue ? "red" : undefined} fw={r.isOverdue ? 600 : undefined}>
                {fmt(r.plannedFrom)} – {fmt(r.plannedTo)}
                {r.isOverdue && " (überfällig)"}
              </Text>
            ),
          },
          {
            accessor: "status",
            title: "Status",
            render: (r) => (
              <Badge color={RENTAL_STATUS_COLORS[r.status]} variant="light">{RENTAL_STATUS_LABELS[r.status]}</Badge>
            ),
          },
          {
            accessor: "feeAmount",
            title: "Gebühr",
            render: (r) =>
              r.feeAmount
                ? `${euro(r.feeAmount)} ${r.feeMode === "deposit" ? (r.feeRefunded ? "(Kaution erstattet)" : "(Kaution)") : ""}`
                : "–",
          },
          {
            accessor: "actions",
            title: "",
            textAlign: "right",
            render: (r) => (
              <Group gap={4} justify="flex-end" wrap="nowrap">
                {r.status === "reserved" && (
                  <>
                    <Button size="xs" onClick={() => action(r, "check-out")}>Ausgeben</Button>
                    <Button size="xs" variant="subtle" color="gray" onClick={() => action(r, "cancel", "Reservierung stornieren?")}>Stornieren</Button>
                  </>
                )}
                {r.status === "out" && (
                  <>
                    <Button size="xs" color="green" onClick={() => action(r, "return")}>Rückgabe</Button>
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <Button size="xs" variant="subtle" color="red" rightSection={<IconChevronDown size={12} />}>Problem</Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => setProblem({ rental: r, kind: "damaged" })}>Beschädigt zurück</Menu.Item>
                        <Menu.Item color="red" onClick={() => setProblem({ rental: r, kind: "lost" })}>Nicht zurückgegeben / verloren</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </>
                )}
              </Group>
            ),
          },
          { accessor: "notes", title: "Notiz", render: (r) => <Text size="xs" c="dimmed" style={{ whiteSpace: "pre-line" }}>{r.notes}</Text> },
        ]}
      />

      <RentalDialog opened={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} />
      <ProblemDialog problem={problem} onClose={() => setProblem(null)} onSaved={load} />
    </Container>
  );
}

function ProblemDialog({ problem, onClose, onSaved }: {
  problem: { rental: RentalRow; kind: "lost" | "damaged" } | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [note, setNote] = useState("");
  const [replacement, setReplacement] = useState<number | string>("");
  const [writeOff, setWriteOff] = useState(true);
  const [refundDeposit, setRefundDeposit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNote("");
    setReplacement("");
    setWriteOff(true);
    setRefundDeposit(false);
  }, [problem]);

  if (!problem) return null;
  const { rental, kind } = problem;
  const hasDeposit = rental.feeMode === "deposit" && !!rental.feeAmount && !rental.feeRefunded;
  const isMember = !!rental.renter;

  const save = async () => {
    setSaving(true);
    try {
      await new HttpClient(`/rental/${rental.id}`).postAction(kind, {
        note: note.trim() || undefined,
        replacementAmount: replacement === "" ? undefined : Number(replacement),
        ...(kind === "damaged" ? { writeOff, refundDeposit } : {}),
      });
      notifications.show({ color: "orange", message: kind === "lost" ? "Als verloren erfasst." : "Als beschädigt erfasst." });
      onSaved();
      onClose();
    } catch (e) {
      notifications.show({ color: "red", message: apiErrorMessage(e, "Speichern fehlgeschlagen.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title={kind === "lost" ? "Nicht zurückgegeben / verloren" : "Beschädigt zurückgegeben"}>
      <Stack>
        <Text size="sm">
          <b>{rental.article.name}</b>{rental.variant.label !== "Standard" ? ` (${rental.variant.label})` : ""} bei <b>{renterLabel(rental)}</b>
        </Text>
        {kind === "lost" ? (
          <Text size="sm" c="dimmed">
            Der Artikel wird aus dem Bestand ausgebucht.{hasDeposit && " Die Kaution wird nicht erstattet."}
          </Text>
        ) : (
          <>
            <Checkbox label="Aus dem Bestand ausbuchen (nicht mehr verwendbar)" checked={writeOff} onChange={(e) => setWriteOff(e.currentTarget.checked)} />
            {hasDeposit && (
              <Checkbox label="Kaution trotzdem erstatten" checked={refundDeposit} onChange={(e) => setRefundDeposit(e.currentTarget.checked)} />
            )}
          </>
        )}
        <NumberInput
          label="Ersatzkosten (€, optional)"
          description={isMember ? "Werden dem Konto des Mitglieds belastet." : "Nur bei Mitgliedern möglich."}
          disabled={!isMember}
          value={replacement}
          onChange={setReplacement}
          min={0}
          decimalScale={2}
          decimalSeparator=","
        />
        <Textarea label="Notiz" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Abbrechen</Button>
          <Button color="red" onClick={save} loading={saving}>Erfassen</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default RentalList;
